import PropTypes from 'prop-types';
import { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useIsMounted } from '@hivebrite/react-hooks';

import { withTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import { followedTopic, followTopic } from '../../actions/followings';
import {
  updateMemberRequestSuccess,
  cancelMemberRequestSuccess,
} from '../../actions/member_requests';
import { fetchTopic } from '../../actions/topics';
import MemberRequest from '../../components/MemberRequest';
import JoinButton from '../../components/TopicHeader/JoinButton';

const TopicMemberRequestContainer = ({
  topic: {
    name,
    id: topicId,
    approval_id: approvalId,
    restricted_access: restrictedAccess,
    automatic_validation: automaticValidation,
    custom_attributes_to_fill: customAttributesToFill,
    is_public_and_requires_user_to_fill_custom_attributes_to_join:
      isPublicAndRequiresUserToFillCustomAttributesToJoin,
  },
  followTopic,
  updateMemberRequestSuccess,
  refreshTopic,
  cancelMemberRequestSuccess,
}) => {
  const isMounted = useIsMounted();
  const [displayRequestModal, setDisplayRequestModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const automaticValivationWithoutCustomAttributes =
    restrictedAccess && automaticValidation && !customAttributesToFill;

  if (
    !restrictedAccess &&
    !isPublicAndRequiresUserToFillCustomAttributesToJoin
  ) {
    return (
      <JoinButton
        isLoading={isLoading}
        onClick={async () => {
          setIsLoading(true);
          try {
            await followTopic();
          } finally {
            isMounted() && setIsLoading(false);
          }
        }}
      />
    );
  }
  if (automaticValivationWithoutCustomAttributes) {
    const sendAutomaticRequest = (topicId) => {
      const approvalParams = {
        approval: { message: __('Automatically Approved') },
      };
      const route = Routes.topics_topic_approvals_path(topicId);

      axios
        .post(route, approvalParams)
        .then(() => {
          refreshTopic();
        })
        .catch((error) => displayXhrError(error.message));
    };

    return (
      <JoinButton
        isLoading={isLoading}
        onClick={() => {
          sendAutomaticRequest(topicId);
        }}
      />
    );
  }

  if (
    approvalId === undefined &&
    !isPublicAndRequiresUserToFillCustomAttributesToJoin
  ) {
    return null;
  }

  return (
    <>
      <JoinButton
        onClick={() => setDisplayRequestModal(true)}
        requestIsPending={!!approvalId}
      />
      {displayRequestModal && (
        <MemberRequest
          isPublicAndRequiresUserToFillCustomAttributesToJoin={
            isPublicAndRequiresUserToFillCustomAttributesToJoin
          }
          approvalId={approvalId}
          onUpdate={(approval) => {
            if (approval.status !== 'approved') {
              updateMemberRequestSuccess(
                automaticValidation ? { id: approval.id } : approval,
              );
            }
            if (restrictedAccess && !automaticValidation) {
              displayNotif(
                'success',
                s__(
                  'Topics|Request submitted. We will send you an email when your application has been processed.',
                ),
              );
            } else {
              displayNotif(
                'success',
                s__('Topics|You have successfully joined %{groupName}.').i__({
                  groupName: name,
                }),
              );
            }
            setDisplayRequestModal(false);
            refreshTopic();
          }}
          onCancel={(displaySuccessNotification = true) => {
            cancelMemberRequestSuccess();
            displaySuccessNotification &&
              displayNotif('success', __('Successfully canceled'));
            setDisplayRequestModal(false);
          }}
          setDisplayRequestModal={setDisplayRequestModal}
        />
      )}
    </>
  );
};

TopicMemberRequestContainer.propTypes = {
  topic: PropTypes.object,
};

const mapDispatchToProps = (dispatch, ownProps, { topicId } = ownProps) => ({
  followTopic: () => dispatch(followTopic(topicId)),
  updateMemberRequestSuccess: (approval) => {
    if (approval.following) {
      dispatch(followedTopic(approval.following));
    } else {
      dispatch(updateMemberRequestSuccess(topicId, approval.id));
    }
  },
  cancelMemberRequestSuccess: () => {
    dispatch(cancelMemberRequestSuccess(topicId));
  },
  refreshTopic: () => {
    dispatch(fetchTopic(topicId));
  },
});

const mapStateToProps = (state, ownProps, { topicId } = ownProps) => ({
  topic: state.topics.getIn(['topics', topicId.toString()])?.toJS(),
});

export default compose(
  withTopicContext,
  connect(mapStateToProps, mapDispatchToProps),
)(TopicMemberRequestContainer);
